/**
 * Custom hook for streaming platform integration
 * 
 * This hook manages all streaming platform connections and interactions including:
 * - Multi-platform support (TikTok, YouTube, Twitch)
 * - Live comment streaming and handling
 * - Stream metrics tracking (viewers, likes, gifts)
 * - Connection state management
 * - BroadcastChannel publishing for inter-window communication
 * 
 * Ported from beemi-react-native for web compatibility.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  StreamingPlatform,
  LiveComment,
  StreamingGift,
  StreamingRoomInfo
} from '../types/streaming';
import { tikTokService } from '../services/streaming/platforms/TikTokStreamingService';
import { twitchService } from '../services/streaming/platforms/TwitchStreamingService';
import { youTubeStreamingService } from '../services/streaming/platforms/YouTubeStreamingService';
import { streamBroadcastService } from '../services/streaming/StreamBroadcastService';
import { useStreamingLimits } from './useStreamingLimits';

interface UseStreamingPlatformParams {
  onGiftNotification?: (gift: StreamingGift) => void;
}

interface StreamingState {
  viewerCount: number;
  likeCount: number;
  platform: StreamingPlatform | 'none';
}

/**
 * Helper to extract user image from RAW Euler structure
 */
function extractImageUrl(userInfo: any): string | undefined {
  if (!userInfo) return undefined;
  if (userInfo.profilePicture?.url?.[0]) return userInfo.profilePicture.url[0];
  if (userInfo.profilePicture?.urls?.[0]) return userInfo.profilePicture.urls[0];
  if (userInfo.avatar_url) return userInfo.avatar_url;
  if (userInfo.avatarUrl) return userInfo.avatarUrl;
  if (userInfo.avatar?.urls?.[0]) return userInfo.avatar.urls[0];
  return undefined;
}

/**
 * Hook for managing streaming platform connections
 */
export const useStreamingPlatform = (params: UseStreamingPlatformParams = {}) => {
  const { onGiftNotification } = params;

  // Streaming limits integration
  const streamingLimits = useStreamingLimits();

  // State
  const [username, setUsername] = useState('');
  const [isConnectedToStream, setIsConnectedToStream] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Not connected');
  const [connectedUsername, setConnectedUsername] = useState<string>('');
  const [connectedPlatform, setConnectedPlatform] = useState<StreamingPlatform | null>(null);
  const [liveComments, setLiveComments] = useState<LiveComment[]>([]);
  const [streamingMetrics, setStreamingMetrics] = useState<StreamingState>({
    viewerCount: 0,
    likeCount: 0,
    platform: 'none'
  });
  const [roomInfo, setRoomInfo] = useState<StreamingRoomInfo | null>(null);

  // Comment batching for performance
  const commentBatchRef = useRef<LiveComment[]>([]);
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Flush accumulated comments to state
   */
  const flushCommentBatch = useCallback(() => {
    if (commentBatchRef.current.length === 0) return;
    
    const batchToAdd = [...commentBatchRef.current];
    commentBatchRef.current = [];
    
    setLiveComments(prev => {
      const combined = [...prev, ...batchToAdd];
      return combined.slice(-100); // Keep last 100
    });
  }, []);

  /**
   * Unified event handler - broadcasts ALL raw events to stream-parser
   * Also handles UI updates for specific event types
   */
  const handleEvent = useCallback((rawMessage: any) => {
    const platform = connectedPlatform || 'tiktok';
    const eventType = rawMessage.type;
    const eventData = rawMessage.data || rawMessage;
    
    console.log(`[useStreamingPlatform] handleEvent: ${eventType} from ${platform}`);
    
    // Handle stats events separately (from MetricsTracker)
    if (eventType === 'stats') {
      // Update streaming metrics with new stats
      setStreamingMetrics(prev => ({
        ...prev,
        viewerCount: eventData.viewerCount || prev.viewerCount,
        likeCount: eventData.likeCount || prev.likeCount
      }));
      
      // Broadcast stats event to game
      streamBroadcastService.publishRawEvent('stats' as any, rawMessage, platform);
      return;
    }
    
    // Determine broadcast type based on Euler event type
    let broadcastType: 'comment' | 'gift' | 'like' | 'join' | 'follow' | 'share' = 'comment';
    if (eventType === 'WebcastChatMessage') broadcastType = 'comment';
    else if (eventType === 'WebcastGiftMessage') broadcastType = 'gift';
    else if (eventType === 'WebcastLikeMessage') broadcastType = 'like';
    else if (eventType === 'WebcastMemberMessage') broadcastType = 'join';
    else if (eventType === 'WebcastSocialMessage') {
      // Check action field: 1 = follow, 3 = share
      if (eventData.action === '1' || eventData.action === 1) {
        broadcastType = 'follow';
      } else if (eventData.action === '3' || eventData.action === 3) {
        broadcastType = 'share';
      } else {
        return; // Ignore other social actions
      }
    }
    
    // Broadcast RAW event to stream-parser (for SDK forwarding to games)
    streamBroadcastService.publishRawEvent(broadcastType, rawMessage, platform);
    
    // Handle UI updates for comment window (not for games)
    if (eventType === 'WebcastLikeMessage') {
      // Trigger footer notification for likes
      const likeData = rawMessage.data || rawMessage;
      const userInfo = likeData.user || {};
      const username = userInfo.uniqueId || userInfo.nickname || 'Viewer';
      const likeCount = likeData.likeCount || likeData.count || 1;
      
      (window as any).streamNotificationQueue?.addLike(username, likeCount);
    }
    else if (eventType === 'WebcastMemberMessage') {
      const memberData = rawMessage.data || rawMessage;
      const userInfo = memberData.user || {};
      const username = userInfo.uniqueId || userInfo.nickname || 'Viewer';
      
      // Trigger footer notification for joins (no chat message)
      (window as any).streamNotificationQueue?.addJoin(username);
    }
    else if (eventType === 'WebcastSocialMessage' && (eventData.action === '1' || eventData.action === 1)) {
      // Handle follow events in UI
      const userInfo = eventData.user || {};
      
      const followComment: LiveComment = {
        id: `follow-${Date.now()}-${Math.random()}`,
        username: userInfo.uniqueId || userInfo.nickname || 'Viewer',
        message: `❤️ followed`,
        comment: '',
        timestamp: new Date().toISOString(),
        type: 'system',
        imageUrl: extractImageUrl(userInfo)
      };
      
      commentBatchRef.current.push(followComment);
      
      // Flush batch
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (commentBatchRef.current.length >= 10) {
        flushCommentBatch();
      } else {
        batchTimeoutRef.current = setTimeout(flushCommentBatch, 200);
      }
    }
    else if (eventType === 'WebcastSocialMessage' && (eventData.action === '3' || eventData.action === 3)) {
      // Handle share events in UI
      const userInfo = eventData.user || {};
      
      const shareComment: LiveComment = {
        id: `share-${Date.now()}-${Math.random()}`,
        username: userInfo.uniqueId || userInfo.nickname || 'Viewer',
        message: `🔗 shared the stream`,
        comment: '',
        timestamp: new Date().toISOString(),
        type: 'system',
        imageUrl: extractImageUrl(userInfo)
      };
      
      commentBatchRef.current.push(shareComment);
      
      // Flush batch
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (commentBatchRef.current.length >= 10) {
        flushCommentBatch();
      } else {
        batchTimeoutRef.current = setTimeout(flushCommentBatch, 200);
      }
    }
  }, [flushCommentBatch, connectedPlatform]);

  /**
   * Handle incoming comments from all platforms
   */
  const handleComment = useCallback((rawComment: any) => {
    const platform = connectedPlatform || 'tiktok';
    
    // Broadcast RAW event FIRST - stream-parser handles SDK formatting
    streamBroadcastService.publishRawEvent('comment', rawComment, platform);
    
    // For UI display only: extract basic info from RAW Euler message
    // Handle full Euler structure: { type: 'WebcastChatMessage', data: {...} }
    const commentData = rawComment.data || rawComment;
    const userInfo = commentData.user || {};
    
    const liveComment: LiveComment = {
      id: commentData.id || rawComment.id || Date.now().toString(),
      username: userInfo.uniqueId || userInfo.nickname || commentData.username || 'Viewer',
      comment: commentData.comment || commentData.message || '',
      message: commentData.comment || commentData.message || '',
      timestamp: commentData.timestamp ? new Date(commentData.timestamp).toISOString() : new Date().toISOString(),
      type: 'comment',
      imageUrl: extractImageUrl(userInfo) || commentData.imageUrl
    };

    // Add to batch (for UI display only)
    commentBatchRef.current.push(liveComment);
    
    // Clear existing timeout
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }
    
    // Flush immediately if batch is large OR after delay
    if (commentBatchRef.current.length >= 10) {
      flushCommentBatch();
    } else {
      batchTimeoutRef.current = setTimeout(flushCommentBatch, 200);
    }
  }, [flushCommentBatch, connectedPlatform]);

  /**
   * Handle incoming gifts  
   * Pass RAW platform events directly (NO transformation!)
   */
  const handleGift = useCallback((rawGift: any) => {
    const platform = connectedPlatform || 'tiktok';
    
    // Broadcast RAW event to stream-parser (NO MAPPING!)
    streamBroadcastService.publishRawEvent('gift', rawGift, platform);
    
    // Trigger notification (for UI) - extract basic info from RAW Euler message
    // Handle full Euler structure: { type: 'WebcastGiftMessage', data: {...} }
    if (onGiftNotification) {
      const giftData = rawGift.data || rawGift;
      const userInfo = giftData.user || {};
      const giftDetails = giftData.giftDetails || giftData.gift || {};
      
      onGiftNotification({
        id: giftData.id || rawGift.id || Date.now().toString(),
        username: userInfo.uniqueId || userInfo.nickname || giftData.username || 'Viewer',
        displayName: userInfo.displayName || userInfo.nickname,
        giftName: giftDetails.giftName || giftDetails.name || giftData.giftName || 'Gift',
        giftCount: giftData.repeatCount || giftData.giftCount || 1,
        value: giftDetails.diamondCount || giftData.diamondCount || giftData.value,
        timestamp: giftData.timestamp || Date.now(),
        platform,
        imageUrl: extractImageUrl(userInfo) || giftData.imageUrl
      });
    }
  }, [onGiftNotification, connectedPlatform]);

  /**
   * Handle incoming likes
   * Pass RAW platform events directly (NO transformation!)
   */
  const handleLike = useCallback((rawLikeData: any) => {
    const platform = connectedPlatform || 'tiktok';

    // Broadcast RAW event to stream-parser (NO MAPPING!)
    streamBroadcastService.publishRawEvent('like', rawLikeData, platform);
  }, [connectedPlatform]);

  /**
   * Handle room info updates
   */
  const handleRoomInfo = useCallback((info: StreamingRoomInfo) => {
    console.log('📡 [Stream] Room info received:', info);
    setRoomInfo(info);
    
    // Broadcast room info to all windows
    streamBroadcastService.publishRoomInfo(info);
  }, []);

  /**
   * Connect to TikTok
   */
  const connectToTikTok = useCallback(async (usernameInput: string): Promise<boolean> => {
    const usernameToUse = usernameInput.trim();
    if (!usernameToUse) {
      setConnectionStatus('Please enter a TikTok username');
      return false;
    }

    setIsConnecting(true);
    setConnectionStatus('Connecting to TikTok live stream...');
    setLiveComments([]);

    try {
      await tikTokService.connect({
        streamerUsername: usernameToUse,
        onEvent: handleEvent,  // NEW: Unified event handler for all raw events
        onComment: handleComment,
        onGift: handleGift,
        onLike: handleLike,
        onRoomInfo: handleRoomInfo,
        onConnect: () => {
          setIsConnectedToStream(true);
          setConnectedUsername(usernameToUse);
          setConnectedPlatform('tiktok');
          setConnectionStatus(`Connected to @${usernameToUse}'s live stream! 🎬`);

          setStreamingMetrics(prev => ({
            ...prev,
            platform: 'tiktok'
          }));

          // Publish connection state
          streamBroadcastService.publishConnectionState({
            state: 'connected',
            platform: 'tiktok',
            username: usernameToUse
          });

          setIsConnecting(false);

          const systemMessage: LiveComment = {
            id: Date.now().toString(),
            username: 'System',
            message: `🎉 Connected to @${usernameToUse}'s live stream!`,
            timestamp: new Date().toISOString(),
            type: 'system'
          };
          setLiveComments(prev => [...prev, systemMessage]);
        },
        onDisconnect: () => {
          setIsConnectedToStream(false);
          setConnectedUsername('');
          setConnectedPlatform(null);
          setConnectionStatus('Disconnected from TikTok live stream');

          setStreamingMetrics({
            viewerCount: 0,
            likeCount: 0,
            platform: 'none'
          });

          streamBroadcastService.publishConnectionState({
            state: 'disconnected',
            platform: null,
            username: null
          });

          setIsConnecting(false);
        },
        onError: (error: any) => {
          const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
          setConnectionStatus(`Error: ${errorMessage}`);
          setIsConnectedToStream(false);
          setIsConnecting(false);
        },
        onConnectionStatus: (status: string) => {
          setConnectionStatus(status);
        },
        onMetricsUpdate: (metrics: any) => {
          setStreamingMetrics({
            viewerCount: metrics.viewerCount || 0,
            likeCount: metrics.likeCount || 0,
            platform: 'tiktok'
          });
          
          streamBroadcastService.publishMetrics({
            viewerCount: metrics.viewerCount || 0,
            likeCount: metrics.likeCount || 0,
            commentCount: metrics.commentCount || 0,
            platform: 'tiktok'
          });
        }
      });

      return true;
    } catch (error: any) {
      console.error('❌ TikTok connection error:', error);
      setConnectionStatus('Connection failed ❌');
      setIsConnectedToStream(false);
      setIsConnecting(false);
      return false;
    }
  }, [handleComment, handleGift, handleLike]);

  /**
   * Connect to Twitch
   */
  const connectToTwitch = useCallback(async (usernameInput: string): Promise<boolean> => {
    const usernameToUse = usernameInput.trim();
    if (!usernameToUse) {
      setConnectionStatus('Please enter a Twitch username');
      return false;
    }

    setIsConnecting(true);
    setConnectionStatus('Connecting to Twitch live stream...');
    setLiveComments([]);

    try {
      await twitchService.connect({
        platform: 'twitch',
        username: usernameToUse,
        streamerUsername: usernameToUse,
        onEvent: handleEvent,  // Handle all events including stats
        onComment: (comment) => {
          handleComment(comment as any);
        },
        onRoomInfo: handleRoomInfo,
        onConnect: () => {
          setIsConnectedToStream(true);
          setConnectedUsername(usernameToUse);
          setConnectedPlatform('twitch');
          setConnectionStatus(`Connected to ${usernameToUse}'s Twitch stream! 🎮`);

          setStreamingMetrics(prev => ({
            ...prev,
            platform: 'twitch'
          }));

          streamBroadcastService.publishConnectionState({
            state: 'connected',
            platform: 'twitch',
            username: usernameToUse
          });

          setIsConnecting(false);

          const systemMessage: LiveComment = {
            id: Date.now().toString(),
            username: 'System',
            message: `🎉 Connected to ${usernameToUse}'s Twitch stream!`,
            timestamp: new Date().toISOString(),
            type: 'system'
          };
          setLiveComments(prev => [...prev, systemMessage]);
        },
        onDisconnect: () => {
          setIsConnectedToStream(false);
          setConnectedUsername('');
          setConnectedPlatform(null);
          setConnectionStatus('Disconnected from Twitch stream');

          setStreamingMetrics({
            viewerCount: 0,
            likeCount: 0,
            platform: 'none'
          });

          streamBroadcastService.publishConnectionState({
            state: 'disconnected',
            platform: null,
            username: null
          });

          setIsConnecting(false);
        },
        onError: (error: any) => {
          const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
          setConnectionStatus(`Error: ${errorMessage}`);
          setIsConnectedToStream(false);
          setIsConnecting(false);
        }
      });

      return true;
    } catch (error: any) {
      console.error('❌ Twitch connection error:', error);
      setConnectionStatus('Twitch connection failed ❌');
      setIsConnectedToStream(false);
      setIsConnecting(false);
      return false;
    }
  }, [handleComment]);

  /**
   * Connect to YouTube
   */
  const connectToYouTube = useCallback(async (usernameInput: string): Promise<boolean> => {
    const usernameToUse = usernameInput.trim();
    if (!usernameToUse) {
      setConnectionStatus('Please enter a YouTube username');
      return false;
    }

    setIsConnecting(true);
    setConnectionStatus('Connecting to YouTube live stream...');
    setLiveComments([]);

    try {
      console.log('📺 [useStreamingPlatform] Connecting to YouTube with handleEvent:', typeof handleEvent, handleEvent);
      await youTubeStreamingService.connect({
        platform: 'youtube',
        username: usernameToUse,
        onEvent: handleEvent,  // Handle all events including stats
        onComment: (comment) => {
          handleComment(comment as any);
        },
        onRoomInfo: handleRoomInfo,
        onConnect: () => {
          setIsConnectedToStream(true);
          setConnectedUsername(usernameToUse);
          setConnectedPlatform('youtube');
          setConnectionStatus(`Connected to ${usernameToUse}'s YouTube stream! 📺`);

          setStreamingMetrics(prev => ({
            ...prev,
            platform: 'youtube'
          }));

          streamBroadcastService.publishConnectionState({
            state: 'connected',
            platform: 'youtube',
            username: usernameToUse
          });

          setIsConnecting(false);

          const systemMessage: LiveComment = {
            id: Date.now().toString(),
            username: 'System',
            message: `🎉 Connected to ${usernameToUse}'s YouTube stream!`,
            timestamp: new Date().toISOString(),
            type: 'system'
          };
          setLiveComments(prev => [...prev, systemMessage]);
        },
        onDisconnect: () => {
          setIsConnectedToStream(false);
          setConnectedUsername('');
          setConnectedPlatform(null);
          setConnectionStatus('Disconnected from YouTube stream');

          setStreamingMetrics({
            viewerCount: 0,
            likeCount: 0,
            platform: 'none'
          });

          streamBroadcastService.publishConnectionState({
            state: 'disconnected',
            platform: null,
            username: null
          });

          setIsConnecting(false);
        },
        onError: (error: any) => {
          const errorMessage = typeof error === 'string' ? error : error?.message || 'Unknown error';
          setConnectionStatus(`Error: ${errorMessage}`);
          setIsConnectedToStream(false);
          setIsConnecting(false);
        },
        onConnectionStatus: (status: string) => {
          setConnectionStatus(status);
        },
        onMetricsUpdate: (metrics) => {
          setStreamingMetrics({
            viewerCount: metrics.viewerCount || 0,
            likeCount: metrics.likeCount || 0,
            platform: 'youtube'
          });
          
          streamBroadcastService.publishMetrics({
            viewerCount: metrics.viewerCount || 0,
            likeCount: metrics.likeCount || 0,
            commentCount: metrics.commentCount || 0,
            platform: 'youtube'
          });
        }
      });

      return true;
    } catch (error: any) {
      console.error('❌ YouTube connection error:', error);
      setConnectionStatus('YouTube connection failed ❌');
      setIsConnectedToStream(false);
      setIsConnecting(false);
      return false;
    }
  }, [handleComment]);

  /**
   * Unified platform connection handler
   */
  const connectToPlatform = useCallback(async (platform: StreamingPlatform, usernameInput: string): Promise<boolean> => {
    setUsername(usernameInput);

    switch (platform) {
      case 'tiktok':
        return await connectToTikTok(usernameInput);
      case 'twitch':
        return await connectToTwitch(usernameInput);
      case 'youtube':
        return await connectToYouTube(usernameInput);
      default:
        return false;
    }
  }, [connectToTikTok, connectToTwitch, connectToYouTube]);

  /**
   * Disconnect from current platform
   */
  const disconnectFromPlatform = useCallback(() => {
    if (!connectedPlatform) return;

    tikTokService.disconnect();
    twitchService.disconnect();
    youTubeStreamingService.disconnect();

    setIsConnectedToStream(false);
    setConnectedUsername('');
    setConnectedPlatform(null);
    setConnectionStatus('Disconnected');

    setStreamingMetrics({
      viewerCount: 0,
      likeCount: 0,
      platform: 'none'
    });

    streamBroadcastService.publishConnectionState({
      state: 'disconnected',
      platform: null,
      username: null
    });

    setLiveComments([]);
  }, [connectedPlatform]);

  /**
   * Start/stop streaming limits tracking based on platform connection
   */
  useEffect(() => {
    if (streamingMetrics.platform !== 'none' && !streamingLimits.isStreaming) {
      console.log('🎬 Starting streaming limits tracking for platform:', streamingMetrics.platform);
      streamingLimits.startStreaming();
    } else if (streamingMetrics.platform === 'none' && streamingLimits.isStreaming) {
      console.log('🛑 Stopping streaming limits tracking');
      streamingLimits.stopStreaming();
    }
  }, [streamingMetrics.platform, streamingLimits.isStreaming, streamingLimits.startStreaming, streamingLimits.stopStreaming]);

  /**
   * Auto-disconnect if limit is exceeded while streaming
   */
  useEffect(() => {
    if (streamingLimits.isLimitExceeded && !streamingLimits.isUnlimited && streamingMetrics.platform !== 'none') {
      console.log('⛔ Streaming limit exceeded, auto-disconnecting...');
      disconnectFromPlatform();
      // Note: UI will show the limit exceeded state via StreamingLimitsOverlay
    }
  }, [streamingLimits.isLimitExceeded, streamingLimits.isUnlimited, streamingMetrics.platform, disconnectFromPlatform]);

  /**
   * Cleanup streaming tracking on unmount
   */
  useEffect(() => {
    return () => {
      if (streamingLimits.isStreaming) {
        streamingLimits.stopStreaming();
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        flushCommentBatch();
      }
    };
  }, [streamingLimits.isStreaming, streamingLimits.stopStreaming, flushCommentBatch]);

  return {
    // State
    username,
    isConnectedToStream,
    isConnecting,
    connectionStatus,
    connectedUsername,
    connectedPlatform,
    liveComments,
    streamingMetrics,
    roomInfo,
    setUsername,
    setLiveComments,

    // Actions
    connectToPlatform,
    disconnectFromPlatform,

    // Individual platform connectors
    connectToTikTok,
    connectToTwitch,
    connectToYouTube,

    // Comment handlers (for direct usage)
    handleComment,
    handleGift,
    handleLike,
    handleRoomInfo,

    // Streaming limits
    streamingLimits
  };
};

